import classNames from 'classnames';
import * as _ from 'lodash-es';
import type { Delta } from 'quill/core';
import Quill from 'quill/core';
import type { Ref } from 'react';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import type { BindingObject } from '../BaseKeyboard';
import { useQuill, useQuillReadOnly } from '../QuillContext';
import type QuillEditor from '../QuillEditor';
import { EditorActions } from '../editorSlice';
import type Mark from '../utils/Mark';
import getRangeFromPoint from '../utils/getRangeFromPoint';
import * as styles from './TableEmbed.module.scss';
import TableToolbar from './TableToolbar';
import type { TableCellProps } from './cell/TableCell';
import TableCell from './cell/TableCell';
import TableCellControl from './cell/TableCellControl';
import getCellBordersForSelection from './getCellBordersForSelection';
import type {
  RearrangingState,
  TableDocumentCell,
  TableDocumentColumn,
  TableDocumentRow,
} from './types';
import useResizableColumns from './useResizableColumns';
import type { Selection } from './useTableSelection';
import useTableSelection, { SelectionType } from './useTableSelection';
import { stringifyCellIdentity } from './utils';

export interface TableAPI {
  selection: Selection | null;
  deleteSelection: () => void;
  forceRenderCellWithQuill: (identity: string, callback?: () => void) => void;
}

type HandlerParams = Parameters<NonNullable<BindingObject['handler']>>;

const useTableBackspaceHandler = (
  quill: QuillEditor | null,
  handler: (range: HandlerParams[0], curContext: HandlerParams[1]) => boolean,
) => {
  const keyboardHandler = useRef(handler);

  useEffect(() => {
    keyboardHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!quill) return undefined;
    const binding: BindingObject = {
      key: ['Backspace', 'Delete'],
      handler: (range, context) => keyboardHandler.current(range, context),
    };

    // @ts-expect-error
    quill.keyboard.prependBinding(binding);
    return () => {
      // @ts-expect-error
      quill.keyboard.removeBinding(binding);
    };
  }, [quill]);
};

const useTransmitStateChange = () => {
  const dispatch = useAppDispatch();

  return useCallback(
    (mark: Mark) => {
      dispatch(EditorActions.transmitStateChange(mark));
    },
    [dispatch],
  );
};

const moveCaretToPosition = (x: number, y: number) => {
  const range = getRangeFromPoint(x, y);
  if (range) {
    const sel = document.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }
};

interface TableProps {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  blot: any;
  selected: boolean;
  rows?: TableDocumentRow[];
  columns?: TableDocumentColumn[];
  cells?: { [identity: string]: TableDocumentCell };
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  span: any;
  tableRef: Ref<TableAPI>;
  onPaste: (e: ClipboardEvent) => void;
  deleteTable: () => void;
  insertRowOrColumn: (index: number, isRow: boolean) => void;
  deleteRowOrColumn: (index: number, isRow: boolean) => void;
  changeCellContent: (
    { rowId, columnId }: { rowId: string; columnId: string },
    delta: Delta,
  ) => void;
  resizeColumn: (columnId: string, width: number) => void;
  rearrangeRowOrColumn: (from: number, to: number, isRow: boolean) => void;
  isEmbedded: boolean;
}

const useForceQuillTableCells = () => {
  const [cellIdentities, setCellIdentities] = useState<Set<string>>(
    () => new Set(),
  );

  const listenersRef = useRef<{ identity: string; callback: () => void }[]>([]);
  useLayoutEffect(() => {
    const listeners = _.remove(listenersRef.current, ({ identity }) =>
      cellIdentities.has(identity),
    );
    listeners.forEach(({ callback }) => callback());
  }, [cellIdentities]);

  return [
    cellIdentities,
    useCallback((identity: string, callback?: () => void) => {
      if (callback) listenersRef.current.push({ identity, callback });
      setCellIdentities((value) => {
        const newValue = new Set(value);
        newValue.add(identity);
        return newValue;
      });
    }, []),
  ] as const;
};

const Table = ({
  blot,
  selected,
  rows = [],
  columns = [],
  span,
  cells = {},
  tableRef,
  onPaste,
  deleteTable,
  insertRowOrColumn,
  deleteRowOrColumn,
  changeCellContent,
  resizeColumn,
  rearrangeRowOrColumn,
  isEmbedded,
}: TableProps) => {
  const { quill } = useQuill();
  const readOnly = useQuillReadOnly();
  const [pagination, setPagination] = useState<{
    pageIndex: number;
    pageSize: number;
  } | null>(null);
  const [rearrangingState, setRearrangingState] =
    useState<RearrangingState | null>(null);
  const ref = useRef<HTMLTableElement | null>(null);
  const {
    selection,
    actions: {
      resetSelection,
      selectRowOrColumn,
      shiftIndex,
      selectCell,
      selectTable,
    },
  } = useTableSelection();

  useEffect(() => {
    if (!selected) {
      resetSelection();
    }
  }, [selected, resetSelection]);

  useEffect(() => {
    if (!rows.length || !columns.length) {
      deleteTable();
    }
  }, [rows, columns, deleteTable]);

  const { minTableWidth, resizedColumn, onColumnResizeStart } =
    useResizableColumns({ resizeColumn, columns });

  const deleteSelection = (): boolean => {
    switch (selection?.type) {
      case SelectionType.Table:
        deleteTable();
        return true;
      case SelectionType.Row:
      case SelectionType.Column: {
        const items =
          selection.type === SelectionType.Row
            ? selection.rows
            : selection.columns;
        const isRow = selection.type === SelectionType.Row;
        items
          .toSorted((a, b) => b - a)
          .forEach((index) => {
            deleteRowOrColumn(index, isRow);
            shiftIndex(index, { isRow, insert: false });
          });
        return true;
      }
      default:
        return false;
    }
  };

  const [forcedCellIdentities, forceRenderCellWithQuill] =
    useForceQuillTableCells();
  useImperativeHandle(tableRef, () => ({
    selection,
    deleteSelection,
    forceRenderCellWithQuill,
  }));

  useTableBackspaceHandler(quill, (_range, context) => {
    if (context.line !== blot) return true;

    const isDeleted = deleteSelection();
    return !isDeleted;
  });

  const handleClickBackground = useCallback(
    (x: number, y: number) => {
      if (!quill) return;
      // Move the cursor to the line above to dismiss table controls.
      // We don't move the cursor to other places to avoid page jumps.
      quill.setSelection(blot.offset(blot.scroll) - 1, 0, Quill.sources.USER);
      moveCaretToPosition(x, y);
    },
    [quill, blot],
  );

  const transmitStateChange = useTransmitStateChange();

  const handleLoadMoreRows = useCallback(() => {
    setPagination((v) =>
      v === null ? v : { ...v, pageIndex: v.pageIndex + 1 },
    );
  }, []);

  useEffect(() => {
    if (selection?.type === SelectionType.Row) {
      transmitStateChange({
        type: 'row',
        index: blot.offset(blot.scroll),
        rows: selection.rows,
      });
    } else if (selection?.type === SelectionType.Column) {
      transmitStateChange({
        type: 'column',
        index: blot.offset(blot.scroll),
        columns: selection.columns,
      });
    }
  }, [selection, transmitStateChange, blot]);

  const handleCellSelectionChange: TableCellProps['onSelectionChange'] =
    useCallback(
      ({ identity, range, yChanged }) => {
        if (!range) return;
        selectCell(identity);
        if (yChanged) {
          transmitStateChange({
            type: 'cell',
            index: blot.offset(blot.scroll),
            cell: { identity, ...range },
          });
        }
      },
      [selectCell, transmitStateChange, blot],
    );

  const pageSize = Math.max(
    1,
    Math.min(
      Table.MAX_ROWS_PER_PAGE,
      Math.floor(Table.MAX_CELLS_PER_PAGE / columns.length),
    ),
  );
  if (pagination === null && rows.length > pageSize) {
    setPagination({ pageSize, pageIndex: 0 });
    return null;
  }

  const truncatedRows = pagination
    ? rows.slice(0, (pagination.pageIndex + 1) * pagination.pageSize)
    : rows;

  return (
    <div
      ref={ref}
      className={classNames(styles.container, {
        [styles.rearrangingState]: rearrangingState,
      })}
      onPasteCapture={({ nativeEvent }) => {
        if (!readOnly) {
          onPaste(nativeEvent);
        }
      }}
    >
      <div className={styles.scrollContainer}>
        <table style={{ minWidth: minTableWidth }}>
          <colgroup>
            {columns.map(({ insert: { id }, attributes }) => {
              const resizingSize =
                resizedColumn?.columnId === id && resizedColumn.width;
              const width =
                resizingSize || (attributes?.width as number | undefined);
              return <col key={id} style={width ? { width } : undefined} />;
            })}
          </colgroup>
          <tbody>
            {truncatedRows.map(({ insert: { id: rowId } }, rowIndex) => {
              const isInLastTruncatedRow =
                truncatedRows.length !== rows.length &&
                rowIndex === truncatedRows.length - 1;

              return (
                <tr key={rowId} data-table-row={rowIndex}>
                  {columns.map(({ insert: { id: columnId } }, columnIndex) => {
                    const identity = stringifyCellIdentity(
                      rowIndex,
                      columnIndex,
                    );
                    const cell = cells[identity];

                    const cellBorders = getCellBordersForSelection(selection, {
                      rowIndex,
                      columnIndex,
                      rowCount: rows.length,
                      columnCount: columns.length,
                    });

                    return (
                      <TableCell
                        key={columnId}
                        cell={cell}
                        rowId={rowId}
                        columnId={columnId}
                        identity={identity}
                        readOnly={readOnly}
                        focused={
                          selection?.type === SelectionType.Cell &&
                          selection.identity === identity
                        }
                        shouldRenderWithQuill={forcedCellIdentities.has(
                          identity,
                        )}
                        onContentChange={changeCellContent}
                        onSelectionChange={handleCellSelectionChange}
                        onColumnResizeStart={onColumnResizeStart}
                        resizing={resizedColumn?.columnId === columnId}
                        onLoadMoreRows={
                          isInLastTruncatedRow ? handleLoadMoreRows : undefined
                        }
                        isEmbedded={isEmbedded}
                        {...cellBorders}
                      >
                        {!readOnly &&
                        selected &&
                        (rowIndex === 0 || columnIndex === 0) ? (
                          <TableCellControl
                            selection={selection}
                            rearrangingState={rearrangingState}
                            rowIndex={rowIndex}
                            columnIndex={columnIndex}
                            lastRow={rowIndex === rows.length - 1}
                            lastColumn={columnIndex === columns.length - 1}
                            onTableSelect={selectTable}
                            onRowOrColumnSelect={selectRowOrColumn}
                            onRowOrColumnInsert={insertRowOrColumn}
                            onIndexShift={shiftIndex}
                            onClickBackground={handleClickBackground}
                            onRearrangingStateChange={setRearrangingState}
                            onRearrange={rearrangeRowOrColumn}
                          />
                        ) : null}
                      </TableCell>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {selected && selection && !readOnly && ref.current && (
        <TableToolbar
          blot={blot}
          span={span}
          tableContainer={ref.current}
          selection={selection}
          onDelete={deleteSelection}
        />
      )}
    </div>
  );
};

Table.MAX_CELLS_PER_PAGE = 5000; // Exposed for testing
Table.MAX_ROWS_PER_PAGE = 150;

export default Table;
